import type { TFunction } from 'i18next';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';

import {
  downloadInsightMembershipExport,
  MEMBERSHIP_EXPORT_MAX_ROWS,
  MembershipExportTooLargeError,
  type MembershipExportParams,
} from '../../services/insight-membership-export.service';

interface Props extends Omit<MembershipExportParams, 'format'> {
  /** Products in the selected chip; `undefined` while the list is still loading. */
  count: number | undefined;
}

/**
 * "Export list" action in the insight-membership drawer footer. Downloads the whole
 * list the drawer is filtered to (selected chip + product category), not just the
 * loaded pages. Disabled — with a tooltip saying why — while counts load, when the
 * chip is empty, or when it's over export-service's row cap.
 */
export function InsightMembershipExportButton({ count, ...params }: Props) {
  const { t } = useTranslation('catalog-operations');
  const [isExporting, setIsExporting] = useState(false);
  const numberFormat = new Intl.NumberFormat();

  const disabledReason =
    count === undefined
      ? t('membershipDrawer.export.disabled.loading')
      : count === 0
        ? t('membershipDrawer.export.disabled.empty')
        : count > MEMBERSHIP_EXPORT_MAX_ROWS
          ? t('membershipDrawer.export.disabled.tooMany', {
              max: numberFormat.format(MEMBERSHIP_EXPORT_MAX_ROWS),
            })
          : null;

  // One download round-trip → a success toast with the file's own row count, or an
  // error toast mapped from the export-service status (413 / 4xx / 5xx).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { filename, rowCount } = await downloadInsightMembershipExport({
        ...params,
        format: 'csv',
      });
      toast.success(
        rowCount === null
          ? t('membershipDrawer.export.success', { file: filename })
          : t('membershipDrawer.export.successWithCount', {
              file: filename,
              count: rowCount,
              formatted: numberFormat.format(rowCount),
            }),
      );
    } catch (error) {
      toast.error(exportErrorMessage(error, t, numberFormat));
    } finally {
      setIsExporting(false);
    }
  };

  const button = (
    <Button
      type="button"
      data-testid="batch-detail-membership-drawer-export-button"
      variant="outline"
      size="sm"
      className={disabledReason ? 'cursor-not-allowed' : 'cursor-pointer'}
      disabled={disabledReason !== null || isExporting}
      isLoading={isExporting}
      onClick={() => {
        void handleExport();
      }}
    >
      {!isExporting && <Download className="size-4" aria-hidden="true" />}
      {isExporting ? t('membershipDrawer.export.pending') : t('membershipDrawer.export.button')}
    </Button>
  );

  if (!disabledReason) return button;
  // A disabled button fires no pointer events, so the tooltip hangs off a wrapper.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Maps an export failure to toast copy: the row cap with both numbers, a retryable
 *  "unavailable" for 5xx, else generic — a 400 here is a web-app bug, not user copy. */
function exportErrorMessage(
  error: unknown,
  t: TFunction<'catalog-operations'>,
  numberFormat: Intl.NumberFormat,
): string {
  if (error instanceof MembershipExportTooLargeError) {
    return error.totalElements === null
      ? t('membershipDrawer.export.error.tooLargeNoCount', {
          max: numberFormat.format(error.maxRows),
        })
      : t('membershipDrawer.export.error.tooLarge', {
          total: numberFormat.format(error.totalElements),
          max: numberFormat.format(error.maxRows),
        });
  }
  if (error instanceof ApiError && (error.statusCode ?? 0) >= 500) {
    return t('membershipDrawer.export.error.unavailable');
  }
  return t('membershipDrawer.export.error.generic');
}
